// IndoorScene.ts - 室内场景系统
// 客厅、厨房、奶奶房间、爷爷房间

import { GameObject, GameObjectType } from './Scene.js';

// 房间类型
export type RoomType = 'living_room' | 'kitchen' | 'grandma_room' | 'daye_room' | 'storage_room' | 'bedroom' | 'bathroom' | 'study_room' | 'attic';

// 房间配置
export interface RoomConfig {
  type: RoomType;
  name: string;
  description: string;
  backgroundColor: string;
  objects: IndoorObject[];
  exits: RoomExit[];
}

// 室内物体
export interface IndoorObject {
  id: string;
  type: GameObjectType | IndoorObjectType;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  interactable: boolean;
  action?: string;
  description: string;
}

// 室内特有的物体类型
export type IndoorObjectType = 
  | 'kang_bed' | 'wardrobe' | 'dresser' | 'mirror'
  | 'sofa' | 'tv' | 'tea_table' | 'cabinet'
  | 'stove' | 'sink' | 'cutting_board' | 'pot'
  | 'bookshelf' | 'desk' | 'lamp' | 'photo_frame'
  | 'grandpa_bed' | 'opium_pipe' | 'newspaper_stack'
  | 'calendar' | 'radio' | 'thermos' | 'rice_cooker'
  | 'back_door' | 'storage_box' | 'tool_rack' | 'grain_jar' | 'pickle_jar'
  | 'bathtub' | 'washbasin' | 'towel_rack' | 'mirror_bath'
  | 'study_table' | 'inkstone' | 'brush_holder' | 'scroll_painting'
  | 'toy_box' | 'doll' | 'marbles' | 'kite_storage'
  | 'old_chest' | 'winter_quilt' | 'summer_quilt' | 'mosquito_net'
  | 'sewing_machine' | 'yarn_basket' | 'fabric_stack'
  | 'medicine_cabinet' | 'herb_jar' | 'bandage_box'
  | 'bird_cage' | 'fish_tank' | 'flower_vase'
  | 'wall_clock' | 'wall_hanging' | 'window_curtain'
  | 'candle_holder' | 'oil_lamp' | 'flashlight'
  | 'broom' | 'dustpan' | 'mop' | 'bucket'
  | 'clothes_hanger' | 'shoe_rack' | 'hat_stand'
  | 'tea_set' | 'wine_jar' | 'snack_plate'
  | 'bamboo_basket' | 'wicker_tray' | 'wooden_barrel'
  | 'grindstone' | 'fire_poker' | 'tongs'
  | 'bedside_table' | 'bedside_lamp' | 'alarm_clock'
  | 'quilt_cabinet' | 'winter_clothes' | 'summer_clothes'
  | 'school_bag' | 'textbook' | 'exercise_book' | 'pencil_box'
  | 'abacus' | 'abacus_beads' | 'calculator_old'
  | 'radio_tape' | 'cassette_tape' | 'record_player'
  | 'family_altar' | 'incense_burner' | 'memorial_tablet'
  | 'lucky_cat' | 'fu_character' | 'red_envelope_stack'
  | 'kang_table' | 'kang_quilt' | 'kang_pillow'
  | 'window_paper' | 'door_god' | 'spring_couplet'
  | 'dust_cloth' | 'feather_duster' | 'polish_wax'
  | 'rice_bowl' | 'chopsticks' | 'spoon' | 'plate_stack'
  | 'wok' | 'steamer' | 'rice_cooker_old' | 'pressure_cooker'
  | 'cleaver' | 'kitchen_knife' | 'scissors_kitchen'
  | 'ladle' | 'spatula' | 'colander' | 'grater'
  | 'rolling_pin' | 'dough_board' | 'noodle_machine'
  | 'soy_sauce_bottle' | 'vinegar_bottle' | 'oil_bottle'
  | 'spice_jar' | 'salt_shaker' | 'sugar_bowl'
  | 'water_kettle' | 'tea_pot' | 'tea_cup_set'
  | 'food_cover' | 'leftover_bowl' | 'pickle_jar_big'
  | 'garlic_string' | 'chili_string' | 'corn_drying'
  | 'broom_corn' | 'dustpan_bamboo' | 'basket_hanging'
  | 'egg_basket' | 'vegetable_basket' | 'fruit_basket'
  | 'meat_hook' | 'sausage_drying' | 'bacon_hanging'
  | 'bean_sprout_jar' | 'tofu_press' | 'noodle_drying_rack'
  | 'flour_sack' | 'rice_sack' | 'grain_storage' | 'toilet' | 'washbasin' | 'towel';

// 房间出口
export interface RoomExit {
  direction: 'left' | 'right' | 'up' | 'down';
  targetRoom: RoomType | 'yard';
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
}

// 房间数据
export const ROOMS: Record<RoomType, RoomConfig> = {
  living_room: {
    type: 'living_room',
    name: '客厅',
    description: '温暖的客厅，一家人团聚的地方',
    backgroundColor: '#F5DEB3',
    objects: [
      {
        id: 'sofa',
        type: 'sofa',
        name: '旧沙发',
        x: 150, y: 200, width: 120, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'rest',
        description: '一张旧沙发，坐上去很软很舒服'
      },
      {
        id: 'tea_table',
        type: 'tea_table',
        name: '茶几',
        x: 180, y: 160, width: 80, height: 50,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '上面放着茶杯和果盘'
      },
      {
        id: 'tv',
        type: 'tv',
        name: '黑白电视机',
        x: 400, y: 180, width: 60, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'watch',
        description: '一台老式黑白电视机，村里没几户人家有'
      },
      {
        id: 'cabinet',
        type: 'cabinet',
        name: '电视柜',
        x: 380, y: 220, width: 100, height: 50,
        zIndex: 4,
        interactable: true,
        action: 'open',
        description: '里面放着一些老照片和杂物'
      },
      {
        id: 'radio_living',
        type: 'radio',
        name: '收音机',
        x: 50, y: 150, width: 40, height: 30,
        zIndex: 6,
        interactable: true,
        action: 'listen',
        description: '奶奶最喜欢的收音机，经常播放戏曲'
      },
      {
        id: 'calendar_living',
        type: 'calendar',
        name: '老黄历',
        x: 100, y: 100, width: 30, height: 40,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '墙上挂着的老黄历，奶奶每天都要翻看'
      },
      {
        id: 'photo_frame',
        type: 'photo_frame',
        name: '全家福',
        x: 300, y: 100, width: 40, height: 50,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '一张泛黄的全家福照片，记录着美好的回忆'
      },
      {
        id: 'wall_clock',
        type: 'wall_clock',
        name: '挂钟',
        x: 500, y: 100, width: 35, height: 50,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '滴答滴答走着的老式挂钟，每到整点会敲响'
      },
      {
        id: 'tea_set',
        type: 'tea_set',
        name: '茶具',
        x: 200, y: 170, width: 40, height: 25,
        zIndex: 7,
        interactable: true,
        action: 'drink',
        description: '一套青花瓷茶具，爷爷最喜欢喝茶'
      },
      {
        id: 'broom',
        type: 'broom',
        name: '扫帚',
        x: 30, y: 280, width: 25, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'use',
        description: '高粱穗扎的扫帚，扫地特别干净'
      },
      {
        id: 'dustpan',
        type: 'dustpan',
        name: '簸箕',
        x: 60, y: 290, width: 30, height: 30,
        zIndex: 6,
        interactable: true,
        action: 'use',
        description: '竹编的簸箕，用来装垃圾'
      },
      {
        id: 'lucky_cat',
        type: 'lucky_cat',
        name: '招财猫',
        x: 520, y: 220, width: 30, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '摇着爪子的陶瓷招财猫，据说能带来好运'
      },
      {
        id: 'fu_character',
        type: 'fu_character',
        name: '福字',
        x: 250, y: 80, width: 40, height: 40,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '倒贴的福字，寓意福到了'
      },
      {
        id: 'flower_vase',
        type: 'flower_vase',
        name: '花瓶',
        x: 50, y: 120, width: 25, height: 35,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '插着几枝野菊花的花瓶'
      }
    ],
    exits: [
      { direction: 'down', targetRoom: 'yard', x: 320, y: 340, width: 160, height: 50, label: '🚪 去院子' },
      { direction: 'left', targetRoom: 'grandma_room', x: 0, y: 150, width: 40, height: 100, label: '👵 奶奶房间' },
      { direction: 'right', targetRoom: 'daye_room', x: 560, y: 150, width: 40, height: 100, label: '👴 爷爷房间' },
      { direction: 'up', targetRoom: 'kitchen', x: 180, y: 0, width: 120, height: 40, label: '🍳 厨房' }
    ]
  },
  
  kitchen: {
    type: 'kitchen',
    name: '厨房',
    description: '奶奶施展厨艺的地方，充满家的味道',
    backgroundColor: '#FFE4B5',
    objects: [
      {
        id: 'stove',
        type: 'stove',
        name: '土灶',
        x: 100, y: 150, width: 100, height: 80,
        zIndex: 5,
        interactable: true,
        action: 'cook',
        description: '传统的土灶，烧柴做饭特别香'
      },
      {
        id: 'cutting_board',
        type: 'cutting_board',
        name: '砧板',
        x: 250, y: 180, width: 60, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'prepare',
        description: '上面放着菜刀和一些蔬菜'
      },
      {
        id: 'pot',
        type: 'pot',
        name: '大铁锅',
        x: 120, y: 140, width: 60, height: 40,
        zIndex: 7,
        interactable: true,
        action: 'cook',
        description: '奶奶的大铁锅，能做一大家子的饭'
      },
      {
        id: 'sink',
        type: 'sink',
        name: '水缸',
        x: 400, y: 160, width: 50, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'wash',
        description: '装满清水的大水缸'
      },
      {
        id: 'rice_cooker',
        type: 'rice_cooker',
        name: '米缸',
        x: 480, y: 140, width: 50, height: 70,
        zIndex: 4,
        interactable: true,
        action: 'look',
        description: '装着今年新米的米缸'
      },
      {
        id: 'thermos',
        type: 'thermos',
        name: '暖水瓶',
        x: 50, y: 200, width: 30, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'drink',
        description: '保温用的暖水瓶，里面装着热水'
      }
    ],
    exits: [
      { direction: 'down', targetRoom: 'living_room', x: 200, y: 350, width: 100, height: 30, label: '去客厅' }
    ]
  },
  
  grandma_room: {
    type: 'grandma_room',
    name: '奶奶的房间',
    description: '奶奶的房间，简单而温馨',
    backgroundColor: '#E6E6FA',
    objects: [
      {
        id: 'kang_bed',
        type: 'kang_bed',
        name: '火炕',
        x: 100, y: 180, width: 150, height: 100,
        zIndex: 4,
        interactable: true,
        action: 'rest',
        description: '温暖的火炕，冬天睡着特别舒服'
      },
      {
        id: 'grandma_wardrobe',
        type: 'wardrobe',
        name: '大衣柜',
        x: 400, y: 150, width: 80, height: 120,
        zIndex: 5,
        interactable: true,
        action: 'open',
        description: '奶奶的大衣柜，里面整齐地叠着衣服'
      },
      {
        id: 'dresser',
        type: 'dresser',
        name: '梳妆台',
        x: 50, y: 200, width: 60, height: 50,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '上面放着梳子和一面小镜子'
      },
      {
        id: 'grandma_mirror',
        type: 'mirror',
        name: '镜子',
        x: 70, y: 180, width: 30, height: 40,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '一面有些斑驳的老镜子'
      },
      {
        id: 'photo_grandma',
        type: 'photo_frame',
        name: '老照片',
        x: 300, y: 150, width: 40, height: 50,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '奶奶年轻时的照片，真漂亮'
      },
      {
        id: 'sewing_box',
        type: 'wicker_basket',
        name: '针线盒',
        x: 200, y: 220, width: 40, height: 30,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '奶奶做针线活用的工具盒'
      }
    ],
    exits: [
      { direction: 'right', targetRoom: 'living_room', x: 570, y: 150, width: 30, height: 100, label: '去客厅' }
    ]
  },
  
  daye_room: {
    type: 'daye_room',
    name: '爷爷的房间',
    description: '爷爷的房间，充满了烟叶和旧书的味道',
    backgroundColor: '#D2B48C',
    objects: [
      {
        id: 'grandpa_bed',
        type: 'grandpa_bed',
        name: '木床',
        x: 400, y: 180, width: 120, height: 100,
        zIndex: 4,
        interactable: true,
        action: 'rest',
        description: '简单的木床，铺着粗布床单'
      },
      {
        id: 'bookshelf',
        type: 'bookshelf',
        name: '书架',
        x: 50, y: 150, width: 80, height: 120,
        zIndex: 5,
        interactable: true,
        action: 'read',
        description: '摆满了旧书和报纸的书架'
      },
      {
        id: 'desk',
        type: 'desk',
        name: '书桌',
        x: 200, y: 200, width: 100, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '爷爷写字看书的地方'
      },
      {
        id: 'lamp',
        type: 'lamp',
        name: '煤油灯',
        x: 220, y: 180, width: 30, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'light',
        description: '一盏老式的煤油灯'
      },
      {
        id: 'opium_pipe',
        type: 'opium_pipe',
        name: '旱烟袋',
        x: 250, y: 210, width: 40, height: 20,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '爷爷的旱烟袋，用了几十年了'
      },
      {
        id: 'newspaper_stack',
        type: 'newspaper_stack',
        name: '报纸堆',
        x: 100, y: 280, width: 80, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'read',
        description: '一摞旧报纸，爷爷喜欢收集这些'
      }
    ],
    exits: [
      { direction: 'left', targetRoom: 'living_room', x: 0, y: 150, width: 30, height: 100, label: '去客厅' }
    ]
  },

  storage_room: {
    type: 'storage_room',
    name: '储藏室',
    description: '存放杂物和粮食的地方，堆满了各种物品',
    backgroundColor: '#D2B48C',
    objects: [
      {
        id: 'grain_jar',
        type: 'grain_jar',
        name: '粮食缸',
        x: 100, y: 150, width: 60, height: 80,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '装满玉米、小米等各种粮食的大缸'
      },
      {
        id: 'pickle_jar',
        type: 'pickle_jar',
        name: '腌菜坛子',
        x: 200, y: 160, width: 50, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '奶奶腌制的咸菜，酸脆可口'
      },
      {
        id: 'tool_rack',
        type: 'tool_rack',
        name: '农具架',
        x: 400, y: 140, width: 80, height: 100,
        zIndex: 4,
        interactable: true,
        action: 'look',
        description: '挂着锄头、镰刀、扁担等各种农具'
      },
      {
        id: 'storage_box',
        type: 'storage_box',
        name: '木箱',
        x: 50, y: 250, width: 70, height: 50,
        zIndex: 6,
        interactable: true,
        action: 'open',
        description: '装着旧衣服和被褥的木箱'
      },
      {
        id: 'winter_quilt',
        type: 'winter_quilt',
        name: '厚棉被',
        x: 300, y: 200, width: 80, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '奶奶亲手做的厚棉被，冬天盖着特别暖和'
      },
      {
        id: 'old_chest',
        type: 'old_chest',
        name: '老樟木箱',
        x: 500, y: 180, width: 70, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'open',
        description: '爷爷留下的老樟木箱，里面可能有宝贝'
      },
      {
        id: 'broom_corn',
        type: 'broom_corn',
        name: '高粱扫帚',
        x: 150, y: 100, width: 40, height: 80,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '用高粱穗扎的扫帚，扫地特别干净'
      },
      {
        id: 'garlic_string',
        type: 'garlic_string',
        name: '大蒜串',
        x: 350, y: 80, width: 30, height: 60,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '一串串晒干的大蒜，挂满了房梁'
      },
      {
        id: 'chili_string',
        type: 'chili_string',
        name: '辣椒串',
        x: 400, y: 80, width: 30, height: 60,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '红彤彤的干辣椒串，看着就有食欲'
      },
      {
        id: 'flour_sack',
        type: 'flour_sack',
        name: '面粉袋',
        x: 480, y: 280, width: 60, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '装着面粉的大袋子，可以做馒头面条'
      }
    ],
    exits: [
      { direction: 'right', targetRoom: 'kitchen', x: 560, y: 150, width: 40, height: 100, label: '去厨房' }
    ]
  },

  bedroom: {
    type: 'bedroom',
    name: '欢欢的房间',
    description: '欢欢的小天地，充满了童年的气息',
    backgroundColor: '#FFF0F5',
    objects: [
      {
        id: 'small_bed',
        type: 'kang_bed',
        name: '小木床',
        x: 100, y: 200, width: 120, height: 80,
        zIndex: 4,
        interactable: true,
        action: 'rest',
        description: '欢欢的小床，床单上印着小动物图案'
      },
      {
        id: 'study_table',
        type: 'study_table',
        name: '书桌',
        x: 300, y: 180, width: 100, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'study',
        description: '欢欢写作业的地方，上面放着课本和文具'
      },
      {
        id: 'school_bag',
        type: 'school_bag',
        name: '书包',
        x: 320, y: 160, width: 40, height: 30,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '红色的帆布书包，是妈妈买的'
      },
      {
        id: 'toy_box',
        type: 'toy_box',
        name: '玩具箱',
        x: 450, y: 250, width: 70, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'open',
        description: '装满了各种玩具的箱子'
      },
      {
        id: 'doll',
        type: 'doll',
        name: '布娃娃',
        x: 150, y: 180, width: 30, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'play',
        description: '欢欢最喜欢的布娃娃，叫小花'
      },
      {
        id: 'marbles',
        type: 'marbles',
        name: '玻璃弹珠',
        x: 400, y: 220, width: 30, height: 20,
        zIndex: 7,
        interactable: true,
        action: 'play',
        description: '五颜六色的玻璃弹珠，和小伙伴玩弹珠用'
      },
      {
        id: 'kite_storage',
        type: 'kite_storage',
        name: '风筝',
        x: 500, y: 100, width: 50, height: 80,
        zIndex: 3,
        interactable: true,
        action: 'look',
        description: '爷爷给做的燕子风筝，春天才能放'
      },
      {
        id: 'bedside_table',
        type: 'bedside_table',
        name: '床头柜',
        x: 80, y: 180, width: 40, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'open',
        description: '放着小台灯和故事书'
      },
      {
        id: 'alarm_clock',
        type: 'alarm_clock',
        name: '小闹钟',
        x: 90, y: 170, width: 20, height: 20,
        zIndex: 7,
        interactable: true,
        action: 'look',
        description: '每天早上叫欢欢起床的小闹钟'
      },
      {
        id: 'window_curtain',
        type: 'window_curtain',
        name: '花窗帘',
        x: 250, y: 80, width: 80, height: 60,
        zIndex: 2,
        interactable: true,
        action: 'look',
        description: '奶奶亲手缝制的花布窗帘'
      }
    ],
    exits: [
      { direction: 'down', targetRoom: 'living_room', x: 300, y: 350, width: 100, height: 30, label: '去客厅' }
    ]
  },

  study_room: {
    type: 'study_room',
    name: '书房',
    description: '安静的书房，书香气息扑面而来',
    backgroundColor: '#F5F5DC',
    objects: [
      {
        id: 'study_desk',
        type: 'desk',
        name: '书案',
        x: 200, y: 200, width: 120, height: 70,
        zIndex: 5,
        interactable: true,
        action: 'write',
        description: '爷爷练字的书案，上面放着文房四宝'
      },
      {
        id: 'inkstone',
        type: 'inkstone',
        name: '砚台',
        x: 220, y: 190, width: 30, height: 20,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '一方老砚台，爷爷用了几十年'
      },
      {
        id: 'brush_holder',
        type: 'brush_holder',
        name: '笔筒',
        x: 250, y: 185, width: 25, height: 35,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '插满了各种毛笔的竹笔筒'
      },
      {
        id: 'bookshelf_study',
        type: 'bookshelf',
        name: '书柜',
        x: 400, y: 150, width: 100, height: 120,
        zIndex: 4,
        interactable: true,
        action: 'read',
        description: '摆满了线装书和字帖的大书柜'
      },
      {
        id: 'scroll_painting',
        type: 'scroll_painting',
        name: '山水画',
        x: 100, y: 100, width: 60, height: 80,
        zIndex: 3,
        interactable: true,
        action: 'admire',
        description: '爷爷收藏的山水画，画的是放牛山'
      },
      {
        id: 'abacus',
        type: 'abacus',
        name: '算盘',
        x: 280, y: 210, width: 40, height: 25,
        zIndex: 6,
        interactable: true,
        action: 'use',
        description: '爷爷算账用的老算盘，噼里啪啦响'
      },
      {
        id: 'tea_set',
        type: 'tea_set',
        name: '茶具',
        x: 350, y: 220, width: 50, height: 30,
        zIndex: 6,
        interactable: true,
        action: 'drink',
        description: '一套青花瓷茶具，爷爷喝茶用的'
      },
      {
        id: 'wall_clock',
        type: 'wall_clock',
        name: '挂钟',
        x: 300, y: 80, width: 40, height: 60,
        zIndex: 3,
        interactable: true,
        action: 'look',
        description: '滴答滴答走着的老式挂钟'
      },
      {
        id: 'flower_vase',
        type: 'flower_vase',
        name: '花瓶',
        x: 480, y: 180, width: 30, height: 40,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '插着几枝梅花的花瓶'
      },
      {
        id: 'lucky_cat',
        type: 'lucky_cat',
        name: '招财猫',
        x: 450, y: 200, width: 25, height: 35,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '摇着爪子的陶瓷招财猫'
      }
    ],
    exits: [
      { direction: 'left', targetRoom: 'daye_room', x: 0, y: 150, width: 40, height: 100, label: '去爷爷房间' }
    ]
  },

  bathroom: {
    type: 'bathroom',
    name: '卫生间',
    description: '简单的农村卫生间',
    backgroundColor: '#B0C4DE',
    objects: [
      {
        id: 'toilet',
        type: 'toilet',
        name: '蹲坑',
        x: 100, y: 200, width: 60, height: 40,
        zIndex: 3,
        interactable: true,
        action: 'use',
        description: '传统的蹲坑'
      },
      {
        id: 'washbasin',
        type: 'washbasin',
        name: '洗脸盆',
        x: 300, y: 180, width: 50, height: 40,
        zIndex: 3,
        interactable: true,
        action: 'use',
        description: '搪瓷洗脸盆'
      },
      {
        id: 'mirror',
        type: 'mirror',
        name: '镜子',
        x: 350, y: 120, width: 40, height: 50,
        zIndex: 2,
        interactable: true,
        action: 'look',
        description: '墙上的一面小镜子'
      },
      {
        id: 'towel',
        type: 'towel',
        name: '毛巾',
        x: 400, y: 150, width: 30, height: 40,
        zIndex: 3,
        interactable: true,
        action: 'use',
        description: '挂着的毛巾'
      }
    ],
    exits: [
      { direction: 'right', targetRoom: 'living_room', x: 560, y: 150, width: 40, height: 100, label: '去客厅' }
    ]
  },

  attic: {
    type: 'attic',
    name: '阁楼',
    description: '神秘的阁楼，堆满了老物件和回忆',
    backgroundColor: '#8B7355',
    objects: [
      {
        id: 'old_trunk',
        type: 'old_chest',
        name: '旧皮箱',
        x: 100, y: 200, width: 80, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'open',
        description: '奶奶出嫁时的皮箱，里面可能有老照片'
      },
      {
        id: 'mosquito_net',
        type: 'mosquito_net',
        name: '旧蚊帐',
        x: 300, y: 150, width: 100, height: 80,
        zIndex: 4,
        interactable: true,
        action: 'look',
        description: '已经发黄的旧蚊帐，夏天用来防蚊子'
      },
      {
        id: 'radio_tape',
        type: 'radio_tape',
        name: '收录机',
        x: 200, y: 180, width: 50, height: 40,
        zIndex: 5,
        interactable: true,
        action: 'play',
        description: '爸爸年轻时买的收录机，还能放磁带'
      },
      {
        id: 'cassette_tapes',
        type: 'cassette_tape',
        name: '磁带盒',
        x: 220, y: 170, width: 30, height: 20,
        zIndex: 6,
        interactable: true,
        action: 'look',
        description: '一盒盒老磁带，有邓丽君的歌'
      },
      {
        id: 'winter_clothes',
        type: 'winter_clothes',
        name: '冬衣箱',
        x: 450, y: 220, width: 70, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'open',
        description: '装着棉衣棉裤的大箱子'
      },
      {
        id: 'bird_cage',
        type: 'bird_cage',
        name: '鸟笼',
        x: 50, y: 150, width: 40, height: 60,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '空的鸟笼，以前养过八哥'
      },
      {
        id: 'family_altar',
        type: 'family_altar',
        name: '祖宗牌位',
        x: 350, y: 100, width: 60, height: 80,
        zIndex: 3,
        interactable: true,
        action: 'pray',
        description: '供奉着祖先牌位的小神龛'
      },
      {
        id: 'incense_burner',
        type: 'incense_burner',
        name: '香炉',
        x: 370, y: 140, width: 20, height: 20,
        zIndex: 4,
        interactable: true,
        action: 'look',
        description: '插着几支香的铜香炉'
      },
      {
        id: 'old_photos',
        type: 'photo_frame',
        name: '老照片',
        x: 150, y: 120, width: 40, height: 50,
        zIndex: 5,
        interactable: true,
        action: 'look',
        description: '泛黄的老照片，记录着家族的历史'
      },
      {
        id: 'dust_cloth',
        type: 'dust_cloth',
        name: '防尘布',
        x: 250, y: 250, width: 80, height: 40,
        zIndex: 6,
        interactable: true,
        action: 'remove',
        description: '盖着家具的防尘布，上面落满了灰尘'
      }
    ],
    exits: [
      { direction: 'down', targetRoom: 'storage_room', x: 300, y: 350, width: 100, height: 30, label: '去储藏室' }
    ]
  }
};

export class IndoorScene {
  private currentRoom: RoomType = 'living_room';
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private scale: number = 1;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private sceneWidth: number = 0;
  private sceneHeight: number = 0;
  
  // 欢欢室内位置
  private huanhuanX: number = 300;
  private huanhuanY: number = 300;
  private targetHuanhuanX: number = 300;
  private targetHuanhuanY: number = 300;
  private time: number = 0;
  
  // 回调
  private onRoomChange?: (room: RoomType) => void;
  private onObjectInteract?: (object: IndoorObject, action: string) => void;
  private onExit?: (targetRoom: RoomType | 'yard') => void;
  private onMove?: (x: number, y: number) => void;

  constructor(canvas?: HTMLCanvasElement) {
    if (canvas) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
    }
  }

  // 设置回调
  setCallbacks(callbacks: {
    onRoomChange?: (room: RoomType) => void;
    onObjectInteract?: (object: IndoorObject, action: string) => void;
    onExit?: (targetRoom: RoomType | 'yard') => void;
    onMove?: (x: number, y: number) => void;
  }): void {
    this.onRoomChange = callbacks.onRoomChange;
    this.onObjectInteract = callbacks.onObjectInteract;
    this.onExit = callbacks.onExit;
    this.onMove = callbacks.onMove;
  }

  // 切换房间
  changeRoom(room: RoomType): void {
    this.currentRoom = room;
    if (this.onRoomChange) {
      this.onRoomChange(room);
    }
  }

  // 获取当前房间
  getCurrentRoom(): RoomConfig {
    return ROOMS[this.currentRoom];
  }

  // 获取当前房间类型
  getCurrentRoomType(): RoomType {
    return this.currentRoom;
  }

  // 设置尺寸 - 房间居中显示
  setSize(width: number, height: number): void {
    // 存储尺寸用于计算居中
    this.sceneWidth = width;
    this.sceneHeight = height;
    
    // 更新canvas尺寸（如果存在）
    if (this.canvas) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    
    // 房间内容居中 - 房间设计尺寸600x400
    const roomWidth = 600;
    const roomHeight = 400;
    
    // 计算居中偏移
    this.offsetX = (width - roomWidth) / 2;
    this.offsetY = (height - roomHeight) / 2;
    this.scale = 1;
    
    console.log('🏠 Indoor scene centered:', { width, height, offsetX: this.offsetX, offsetY: this.offsetY });
  }

  // 处理点击 - 简化为直接使用屏幕坐标减去居中偏移
  handleClick(x: number, y: number): void {
    // 房间是居中绘制的
    const roomWidth = 600;
    const roomHeight = 400;
    const offsetX = (this.sceneWidth - roomWidth) / 2;
    const offsetY = (this.sceneHeight - roomHeight) / 2;
    
    const roomX = x - offsetX;
    const roomY = y - offsetY;
    
    // 检查是否点击了出口
    const room = this.getCurrentRoom();
    for (const exit of room.exits) {
      if (this.isPointInRect(roomX, roomY, exit.x, exit.y, exit.width, exit.height)) {
        if (this.onExit) this.onExit(exit.targetRoom);
        return;
      }
    }

    // 检查是否点击了物体
    const sortedObjects = [...room.objects].sort((a, b) => b.zIndex - a.zIndex);
    for (const obj of sortedObjects) {
      if (obj.interactable && this.isPointInRect(roomX, roomY, obj.x, obj.y, obj.width, obj.height)) {
        if (this.onObjectInteract) this.onObjectInteract(obj, obj.action || 'look');
        return;
      }
    }
    
    // 点击空白处 - 移动欢欢到该位置
    this.targetHuanhuanX = roomX;
    this.targetHuanhuanY = roomY;
    if (this.onMove) this.onMove(roomX, roomY);
  }

  // 更新欢欢位置（平滑移动）- 参考院子的移动方式
  update(deltaTime: number): void {
    this.time += deltaTime;
    
    // 使用与院子相同的速度公式
    const speed = 150 * deltaTime;
    const dx = this.targetHuanhuanX - this.huanhuanX;
    const dy = this.targetHuanhuanY - this.huanhuanY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist > 5) {
      this.huanhuanX += (dx / dist) * speed;
      this.huanhuanY += (dy / dist) * speed;
    } else {
      this.huanhuanX = this.targetHuanhuanX;
      this.huanhuanY = this.targetHuanhuanY;
    }
  }

  // 获取欢欢当前位置
  getHuanhuanPosition(): { x: number, y: number } {
    return { x: this.huanhuanX, y: this.huanhuanY };
  }

  // 获取缩放和偏移（供外部使用）
  getScale(): number { return this.scale; }
  getOffsetX(): number { return this.offsetX; }
  getOffsetY(): number { return this.offsetY; }

  // 检查点是否在矩形内
  private isPointInRect(px: number, py: number, rx: number, ry: number, rw: number, rh: number): boolean {
    return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
  }

  // 渲染房间
  render(ctx: CanvasRenderingContext2D, huanhuanPos?: {x: number, y: number}): void {
    const room = this.getCurrentRoom();
    
    // 保存上下文
    ctx.save();
    
    // 不缩放，直接按原始坐标绘制
    // 绘制背景
    ctx.fillStyle = room.backgroundColor;
    ctx.fillRect(0, 0, 600, 400);

    // 绘制地板纹理
    this.renderFloorTexture(ctx);

    // 绘制墙壁边框
    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 8;
    ctx.strokeRect(0, 0, 600, 400);

    // 按zIndex排序并绘制物体
    const sortedObjects = [...room.objects].sort((a, b) => a.zIndex - b.zIndex);
    for (const obj of sortedObjects) {
      this.renderObject(ctx, obj);
    }

    // 绘制欢欢（使用室内位置 + 居中偏移）
    this.renderHuanhuanIndoor(ctx, this.huanhuanX + this.offsetX, this.huanhuanY + this.offsetY);

    // 绘制出口
    for (const exit of room.exits) {
      this.renderExit(ctx, exit);
    }

    // 绘制房间名称
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.beginPath();
    ctx.roundRect(20, 20, 120, 35, 8);
    ctx.fill();
    
    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(room.name, 80, 43);

    ctx.restore();
  }

  // 渲染室内的欢欢
  private renderHuanhuanIndoor(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    // 欢欢 - 与室外一致的小女孩图形
    const bounce = Math.abs(Math.sin(this.time * 8)) * 3;
    
    // 腿
    ctx.fillStyle = '#F5DEB3'; // 皮肤
    ctx.fillRect(x - 6, y - 15 + bounce, 4, 12);
    ctx.fillRect(x + 2, y - 15 + bounce, 4, 12);
    
    // 鞋
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x - 7, y - 5 + bounce, 6, 4);
    ctx.fillRect(x + 1, y - 5 + bounce, 6, 4);
    
    // 身体 - 粉色裙子
    ctx.fillStyle = '#FF69B4';
    ctx.beginPath();
    ctx.moveTo(x - 15, y - 15 + bounce);
    ctx.lineTo(x + 15, y - 15 + bounce);
    ctx.lineTo(x + 12, y - 40 + bounce);
    ctx.lineTo(x - 12, y - 40 + bounce);
    ctx.closePath();
    ctx.fill();
    
    // 袖子
    ctx.fillStyle = '#FFB6C1';
    ctx.fillRect(x - 10, y - 52 + bounce, 20, 15);
    
    // 手臂
    ctx.fillStyle = '#F5DEB3';
    ctx.fillRect(x - 14, y - 48 + bounce, 4, 12);
    ctx.fillRect(x + 10, y - 48 + bounce, 4, 12);
    
    // 头
    ctx.fillStyle = '#F5DEB3';
    ctx.beginPath();
    ctx.arc(x, y - 62 + bounce, 13, 0, Math.PI * 2);
    ctx.fill();
    
    // 头发 - 棕色
    ctx.fillStyle = '#2F1810';
    ctx.beginPath();
    ctx.arc(x, y - 65, 14, Math.PI, 0);
    ctx.fill();
    // 辫子
    ctx.beginPath();
    ctx.ellipse(x - 12, y - 55 + bounce, 5, 10, -0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + 12, y - 55 + bounce, 5, 10, 0.3, 0, Math.PI * 2);
    ctx.fill();
    
    // 眼睛
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(x - 4, y - 62 + bounce, 2, 0, Math.PI * 2);
    ctx.arc(x + 4, y - 62 + bounce, 2, 0, Math.PI * 2);
    ctx.fill();
    
    // 腮红
    ctx.fillStyle = '#FF6B6B';
    ctx.beginPath();
    ctx.arc(x, y - 55 + bounce, 3, 0, Math.PI);
    ctx.fill();
  }

  // 渲染地板纹理
  private renderFloorTexture(ctx: CanvasRenderingContext2D): void {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    
    // 绘制简单的地板线条
    for (let i = 0; i < 600; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, 400);
      ctx.stroke();
    }
    for (let i = 0; i < 400; i += 40) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(600, i);
      ctx.stroke();
    }
  }

  // 渲染物体
  private renderObject(ctx: CanvasRenderingContext2D, obj: IndoorObject): void {
    const x = obj.x;
    const y = obj.y;
    const w = obj.width;
    const h = obj.height;

    // 根据物体类型绘制不同的图形
    switch (obj.type) {
      case 'sofa':
        this.renderSofa(ctx, x, y, w, h);
        break;
      case 'tea_table':
        this.renderTeaTable(ctx, x, y, w, h);
        break;
      case 'tv':
        this.renderTV(ctx, x, y, w, h);
        break;
      case 'cabinet':
        this.renderCabinet(ctx, x, y, w, h);
        break;
      case 'kang_bed':
        this.renderKangBed(ctx, x, y, w, h);
        break;
      case 'wardrobe':
        this.renderWardrobe(ctx, x, y, w, h);
        break;
      case 'stove':
        this.renderStove(ctx, x, y, w, h);
        break;
      case 'bookshelf':
        this.renderBookshelf(ctx, x, y, w, h);
        break;
      case 'grandpa_bed':
        this.renderBed(ctx, x, y, w, h, '#8B4513');
        break;
      case 'desk':
        this.renderDesk(ctx, x, y, w, h);
        break;
      default:
        // 默认渲染一个矩形框
        ctx.fillStyle = '#A0A0A0';
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = '#666';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, w, h);
    }

    // 如果是可交互物体，添加高亮边框
    if (obj.interactable) {
      ctx.strokeStyle = 'rgba(139, 195, 74, 0.5)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x - 2, y - 2, w + 4, h + 4);
    }
  }

  // 渲染沙发
  private renderSofa(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // 沙发底座
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y + h * 0.3, w, h * 0.7);
    
    // 沙发靠背
    ctx.fillStyle = '#A0522D';
    ctx.fillRect(x, y, w, h * 0.5);
    
    // 扶手
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x - 5, y + h * 0.3, 15, h * 0.5);
    ctx.fillRect(x + w - 10, y + h * 0.3, 15, h * 0.5);
  }

  // 渲染茶几
  private renderTeaTable(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = '#654321';
    ctx.fillRect(x, y + h * 0.3, w, h * 0.1);
    ctx.fillRect(x + 5, y + h * 0.4, 10, h * 0.6);
    ctx.fillRect(x + w - 15, y + h * 0.4, 10, h * 0.6);
    
    // 茶杯
    ctx.fillStyle = '#FFF';
    ctx.beginPath();
    ctx.arc(x + w * 0.3, y + h * 0.2, 8, 0, Math.PI * 2);
    ctx.fill();
  }

  // 渲染电视机
  private renderTV(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // 电视机身
    ctx.fillStyle = '#333';
    ctx.fillRect(x, y, w, h);
    
    // 屏幕
    ctx.fillStyle = '#222';
    ctx.fillRect(x + 5, y + 5, w - 10, h - 15);
    
    // 天线
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + w * 0.3, y);
    ctx.lineTo(x + w * 0.2, y - 15);
    ctx.moveTo(x + w * 0.7, y);
    ctx.lineTo(x + w * 0.8, y - 15);
    ctx.stroke();
  }

  // 渲染柜子
  private renderCabinet(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y, w, h);
    
    // 柜门
    ctx.strokeStyle = '#654321';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x + w / 2, y);
    ctx.lineTo(x + w / 2, y + h);
    ctx.stroke();
    
    // 把手
    ctx.fillStyle = '#FFD700';
    ctx.beginPath();
    ctx.arc(x + w * 0.25, y + h * 0.5, 4, 0, Math.PI * 2);
    ctx.arc(x + w * 0.75, y + h * 0.5, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  // 渲染火炕
  private renderKangBed(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // 炕体
    ctx.fillStyle = '#D2B48C';
    ctx.fillRect(x, y, w, h);
    
    // 褥子
    ctx.fillStyle = '#CD853F';
    ctx.fillRect(x + 10, y + 10, w - 20, h - 20);
    
    // 枕头
    ctx.fillStyle = '#FFF';
    ctx.fillRect(x + 20, y + 15, 30, 20);
  }

  // 渲染衣柜
  private renderWardrobe(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y, w, h);
    
    // 柜门线条
    ctx.strokeStyle = '#654321';
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 3, y + 3, w - 6, h - 6);
    ctx.beginPath();
    ctx.moveTo(x + w / 2, y + 3);
    ctx.lineTo(x + w / 2, y + h - 3);
    ctx.stroke();
    
    // 把手
    ctx.fillStyle = '#FFD700';
    ctx.fillRect(x + w * 0.25 - 3, y + h * 0.5 - 8, 6, 16);
    ctx.fillRect(x + w * 0.75 - 3, y + h * 0.5 - 8, 6, 16);
  }

  // 渲染土灶
  private renderStove(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // 灶台
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y, w, h);
    
    // 灶眼
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(x + w * 0.3, y + h * 0.3, 15, 0, Math.PI * 2);
    ctx.arc(x + w * 0.7, y + h * 0.3, 15, 0, Math.PI * 2);
    ctx.fill();
    
    // 烟囱
    ctx.fillStyle = '#666';
    ctx.fillRect(x + w * 0.4, y - 30, w * 0.2, 30);
  }

  // 渲染书架
  private renderBookshelf(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y, w, h);
    
    // 隔板
    ctx.strokeStyle = '#654321';
    ctx.lineWidth = 2;
    for (let i = 1; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(x, y + i * h / 4);
      ctx.lineTo(x + w, y + i * h / 4);
      ctx.stroke();
    }
    
    // 书本
    ctx.fillStyle = '#4169E1';
    for (let i = 0; i < 3; i++) {
      ctx.fillRect(x + 5 + i * 15, y + h * 0.75 + 5, 10, h * 0.25 - 10);
    }
  }

  // 渲染床
  private renderBed(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string): void {
    // 床架
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
    
    // 床垫
    ctx.fillStyle = '#DEB887';
    ctx.fillRect(x + 5, y + 5, w - 10, h - 15);
    
    // 枕头
    ctx.fillStyle = '#FFF';
    ctx.fillRect(x + 15, y + 10, 35, 25);
  }

  // 渲染书桌
  private renderDesk(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // 桌面
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x, y, w, h * 0.3);
    
    // 桌腿
    ctx.fillRect(x + 10, y + h * 0.3, 10, h * 0.7);
    ctx.fillRect(x + w - 20, y + h * 0.3, 10, h * 0.7);
  }

  // 渲染出口
  private renderExit(ctx: CanvasRenderingContext2D, exit: RoomExit): void {
    const x = exit.x;
    const y = exit.y;
    const w = exit.width;
    const h = exit.height;

    // 出口背景 - 更醒目
    ctx.fillStyle = 'rgba(139, 195, 74, 0.5)';
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 8);
    ctx.fill();
    
    // 边框
    ctx.strokeStyle = '#8BC34A';
    ctx.lineWidth = 3;
    ctx.stroke();

    // 出口标签背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    const labelWidth = Math.max(exit.label.length * 14 + 16, 80);
    ctx.beginPath();
    ctx.roundRect(x + (w - labelWidth) / 2, y + (h - 25) / 2, labelWidth, 28, 6);
    ctx.fill();

    // 出口标签
    ctx.fillStyle = '#FFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(exit.label, x + w / 2, y + h / 2);
  }

  // 渲染箭头
  private renderArrow(ctx: CanvasRenderingContext2D, direction: string, x: number, y: number): void {
    ctx.beginPath();
    switch (direction) {
      case 'up':
        ctx.moveTo(x, y);
        ctx.lineTo(x - 8, y + 10);
        ctx.moveTo(x, y);
        ctx.lineTo(x + 8, y + 10);
        break;
      case 'down':
        ctx.moveTo(x, y);
        ctx.lineTo(x - 8, y - 10);
        ctx.moveTo(x, y);
        ctx.lineTo(x + 8, y - 10);
        break;
      case 'left':
        ctx.moveTo(x, y);
        ctx.lineTo(x + 10, y - 8);
        ctx.moveTo(x, y);
        ctx.lineTo(x + 10, y + 8);
        break;
      case 'right':
        ctx.moveTo(x, y);
        ctx.lineTo(x - 10, y - 8);
        ctx.moveTo(x, y);
        ctx.lineTo(x - 10, y + 8);
        break;
    }
    ctx.stroke();
  }

  // 获取指定房间
  getRoom(roomType: RoomType): RoomConfig {
    return ROOMS[roomType];
  }

  // 获取所有房间
  getAllRooms(): RoomConfig[] {
    return Object.values(ROOMS);
  }
}